import React, { useEffect, useRef } from "react"
import cn from "classnames"
import type { actions } from "@/signomatic/store/actions"
import { withTranslation, type WithTranslation } from "react-i18next"
import { isTablet } from "@/common/util"
import type Sign from "@/signomatic/app/sign"
import { CheckmarkLarge, CloseLarge } from "@/skylticons"
import HelpCenter from "@/help_center/components/help_center"

export interface ToolboxItemOwnProps {
  sign?: Sign
  overflow?: boolean
  className?: string
  index?: number
  children?: React.ReactNode
  forwardedRef?: React.RefObject<HTMLDivElement> | null
  mobile?: boolean
  doneLabel?: string
  subToolbox?: string | null
}

export interface ToolboxItemStateProps {
  setToolbox: typeof actions.setToolbox
  setToolboxHeight: typeof actions.setToolboxHeight
}

type ToolboxItemProps = ToolboxItemOwnProps & ToolboxItemStateProps & WithTranslation

const ToolboxItem: React.FC<ToolboxItemProps> = ({
  children,
  className,
  overflow,
  forwardedRef,
  mobile,
  index,
  doneLabel,
  t,
  sign,
  subToolbox,
  setToolbox,
  setToolboxHeight,
}) => {
  const menurow = useRef(document.querySelector<HTMLElement>(".menurow"))
  const menurowFiller = useRef(document.querySelector<HTMLElement>(".menurow-filler"))
  const heightAuto = className === "toolbox-fixmethod" && !!subToolbox

  const handleDone = (e: React.MouseEvent) => {
    e.stopPropagation()
    sign?.deleteEmptyObjects()

    if (isTablet() && sign) {
      setToolboxHeight(0)
      if (menurow.current) menurow.current.style.display = "flex"
      if (menurowFiller.current) menurowFiller.current.style.display = "block"
    }

    setToolbox(null, index)
  }

  useEffect(() => {
    if (isTablet()) {
      setToolboxHeight(document.querySelector(".toolbox")?.clientHeight || 0)
      if (menurow.current) menurow.current.style.display = "none"
      if (menurowFiller.current) menurowFiller.current.style.display = "none"
    }
  }, [setToolboxHeight])

  return (
    <div
      className={cn(
        "toolbox visible-scrollbar with-done pointer-events-auto absolute bottom-0 left-0 right-0 z-40 flex flex-col bg-white shadow-[0_2px_4px_rgba(0,0,0,0.5)] lg:relative lg:w-80 first:lg:h-full first:lg:shadow-none xl:relative xl:bottom-[initial] xl:left-[initial] xl:right-[initial] xl:top-[initial] xl:w-96 [&:nth-child(2)]:shadow-[0_2px_4px_rgba(0,0,0,0.5)] lg:[&:nth-child(2)]:absolute lg:[&:nth-child(2)]:bottom-auto lg:[&:nth-child(2)]:left-[434px] lg:[&:nth-child(2)]:h-[calc(100vh-280px)] lg:[&:nth-child(2)]:max-h-[640px] xl:[&:nth-child(2)]:left-[538px]",
        className,
        {
          overflow,
          "lg:[&:nth-child(2)]:h-auto": heightAuto,
          "h-full": className === "toolbox-products",
        }
      )}
      ref={forwardedRef}
    >
      {mobile && (
        <div id="help-widget">
          <HelpCenter signomatic />
        </div>
      )}
      <div className="toolbox-contents z-10">{children}</div>
      <div className="button-group z-20">
        {(index || 0) >= 1 ? (
          <div
            className="toolbox-done button button-primary button-medium full-width icon-left squared mb-0"
            onClick={handleDone}
          >
            <CloseLarge />
            <span>{t("misc.close")}</span>
          </div>
        ) : (
          <div
            className="toolbox-done button button-primary button-medium full-width icon-left squared mb-0"
            onClick={handleDone}
          >
            <CheckmarkLarge />
            <span>{doneLabel || t("toolbox.done")}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default withTranslation()(ToolboxItem)
